import React, { useCallback, useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import axios from "axios";
import StorageFactory from "../../../Util/StorageFactory";
import {
  SESSION_USER_ID_KEY,
} from "../../../Util/VMConstants";
import {
  API_ADD_CHECKLIST_UPDATE,
  API_GET_ASSIGNED_CHECKLIST,
  API_GET_CHECKLIST_HISTORY,
} from "../../../Util/URLConstants";
import { align_center, dataGridStyle, VM_Colors } from "../../../Styles/VMStyles";
import MUITypography from "../../../Components/MUITypography";
import { toast } from "react-toastify";
import { formatDate, getPriorityColor } from "../../../Util/ConstnatUtils";
import StatusButton from "../../../Components/StatusButton";
import { CalendarToday } from "@mui/icons-material";
import ReusableDrawer from "../../../Components/ReusableDrawer";
import CheckListHistory from "./CheckListHistory";
import MuiTypographyMandatory from "../../../Components/Custom/MuiTypographyMandatory";
import StyledInputField from "../../../Components/Custom/StyledInputField";
import ReusableDataGrid from "../../../Components/ReusableDataGrid";

const checklistGridSx = {
  ...dataGridStyle,
  border: "none",
  "& .MuiDataGrid-root": { border: "none" },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: VM_Colors.primary,
    minHeight: "48px !important",
    maxHeight: "48px !important",
  },
  "& .MuiDataGrid-row:nth-of-type(even)": {
    backgroundColor: VM_Colors.white2,
  },
  "& .MuiDataGrid-row:hover": {
    backgroundColor: "rgba(30, 58, 138, 0.06) !important",
  },
  "& .MuiDataGrid-cell": {
    ...dataGridStyle["& .MuiDataGrid-cell"],
    alignItems: "center",
    py: 1,
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: `1px solid ${VM_Colors.light_gray}`,
    minHeight: 52,
  },
};

function CheckListUpdate() {
  const [checkList, setCheckList] = useState([]);
  const [gridLoading, setGridLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [updatedCheckList, setUpdatedCheckList] = useState(null);

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [pointStatus, setPointStatus] = useState({});
  const [comments, setComments] = useState("");
  const [history, setHistory] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [selectedCheckListId, setSelectedCheckListID] = useState(null)
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false)

  const sessionFactory = new StorageFactory("session");
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);

  const fetchCheckList = useCallback(async () => {
    try {
      setGridLoading(true);
      const res = await axios.get(
        `${API_GET_ASSIGNED_CHECKLIST}?user_id=${userID}`
      );
      const formattedData = (res.data?.data || []).map((item, idx) => ({
        ...item,
        s_no: idx + 1,
      }));
      setCheckList(formattedData);
    } catch (error) {
      console.error("Error fetching checklist:", error);
    } finally {
      setGridLoading(false);
    }
  }, [userID]);

  const fetchChecklistHistory = async (checklistId) => {
    try {
      setHistoryLoading(true);
      const res = await axios.get(
        `${API_GET_CHECKLIST_HISTORY}?checklist_id=${checklistId}`
      );
      const data = res.data?.data || [];
      setHistory(data);

      if (data.length > 0) {
        const latest = data[0]; // Assume first is latest
        setUpdatedCheckList(latest);

        if (latest.checklist_points_status) {
          setPointStatus(latest.checklist_points_status);
        }
        // if (latest.comments) {
        //   setComments(latest.comments);
        // }
      } else {
        if (selectedTask?.checklist_points) {
          const initialStatus = {};
          selectedTask.checklist_points.forEach((point) => {
            initialStatus[point] = null;
          });
          setPointStatus(initialStatus);
        }
        setComments("");
      }
    } catch (error) {
      console.error("Error fetching history:", error);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (userID) fetchCheckList();
  }, [userID, fetchCheckList]);

  const handleOpenDialog = useCallback((task) => {
    setSelectedTask(task);
    setPointStatus({});
    setComments("");
    setOpenDialog(true);
    fetchChecklistHistory(task.id);
  }, []);

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedTask(null);
    setPointStatus({});
    setComments("");
    setHistory([]);
    setUpdatedCheckList(null);
  };

  const handlePointStatusChange = (point, value) => {
    setPointStatus((prev) => ({
      ...prev,
      [point]: value,
    }));
  };

  const handleSubmitUpdate = async () => {
    const allAnswered = Object.values(pointStatus).every(
      (v) => v === "Yes" || v === "No"
    );
    if (!allAnswered) {
      toast.error("⚠️ Please mark Yes or No for all checklist points.");
      return;
    }
    if (!comments.trim()) {
      toast.error("⚠️ Please add comments.");
      return;
    }

    const reqBody = {
      user_id: userID,
      vm_checklist_id: selectedTask.id,
      checklist_points_status: pointStatus,
      comments: comments.trim(),
    };

    try {
      setSubmitting(true);
      const { data } = await axios.post(API_ADD_CHECKLIST_UPDATE, reqBody, {
        headers: { "Content-Type": "application/json" },
      });

      if (data?.status_code === 200) {
        toast.success("✅ Checklist updated successfully!");
        handleCloseDialog();
        fetchCheckList();
      } else {
        throw new Error(data?.message || "Failed to update checklist.");
      }
    } catch (error) {
      console.error("❌ Error updating checklist:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to update checklist."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const renderTextCell = (value) => (
    <Typography
      variant="body2"
      sx={{
        lineHeight: 1.4,
        wordBreak: "break-word",
        color: value ? "text.primary" : "text.disabled",
      }}
    >
      {value || "—"}
    </Typography>
  );

  const columns = useMemo(
    () => [
      {
        field: "s_no",
        headerName: "S.No",
        width: 64,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: "checklist_name",
        headerName: "Checklist",
        flex: 1.2,
        minWidth: 140,
        sortable: false,
        renderCell: (params) => renderTextCell(params.value),
      },
      {
        field: "priority",
        headerName: "Priority",
        width: 118,
        sortable: false,
        renderCell: (params) => (
          <Chip
            label={params.value || "—"}
            color={getPriorityColor(params.value)}
            variant="outlined"
            size="small"
            sx={{ minWidth: 88, fontWeight: 600, fontSize: "0.75rem" }}
          />
        ),
      },
      {
        field: "checklist_status",
        headerName: "Status",
        width: 130,
        sortable: false,
        renderCell: (params) => <StatusButton status={params.value} />,
      },
      {
        field: "due_date",
        headerName: "Due Date",
        flex: 0.9,
        minWidth: 130,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <CalendarToday fontSize="small" color="action" />
            <Typography variant="body2">
              {params.value ? formatDate(params.value) : "—"}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "history",
        headerName: "History",
        width: 80,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Tooltip title="View history">
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                setSelectedCheckListID(params.row.id);
                setHistoryDrawerOpen(true);
              }}
              aria-label="View checklist history"
            >
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
      {
        field: "actions",
        headerName: "Update",
        width: 88,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Tooltip title="Update checklist">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleOpenDialog(params.row)}
              aria-label="Update checklist"
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [handleOpenDialog]
  );

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, m: { xs: 1, sm: 2 } }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        My Checklist
      </Typography>

      <Box
        sx={{
          width: "100%",
          minWidth: 0,
          bgcolor: "#fff",
          borderRadius: 2,
          border: `1px solid ${VM_Colors.light_gray}`,
          overflow: "hidden",
          boxShadow: "0 1px 4px rgba(15, 23, 42, 0.08)",
        }}
      >
        <ReusableDataGrid
          rows={checkList}
          columns={columns}
          loading={gridLoading}
          getRowId={(row) => row.id}
          paginationMode="client"
          rowCount={checkList.length}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
          pageSizeOptions={[5, 10, 25]}
          autoHeight
          getRowHeight={() => 56}
          customSx={checklistGridSx}
        />
      </Box>

      {/* Update Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <MUITypography sx={align_center}> Update Checklist </MUITypography>
        </DialogTitle>
        <DialogContent>
          {selectedTask && (
            <>
              <Stack direction={'row'}>
                <MuiTypographyMandatory label="Checklist : "  />
                <MUITypography  fontWeight={700} label={selectedTask.checklist_name}  sx={{color:VM_Colors.primary,ml:1 }}/>
            
              </Stack>

              <Box mt={3}>
                {selectedTask.checklist_points?.map((point, idx) => (
                  <Stack
                    key={idx}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ borderBottom: "1px solid #ddd", py: 1 }}
                  >
                    <Typography>{point}</Typography>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant={
                          pointStatus[point] === "Yes" ? "contained" : "outlined"
                        }
                        color="success"
                        onClick={() => handlePointStatusChange(point, "Yes")}
                      >
                        Yes
                      </Button>
                      <Button
                        variant={
                          pointStatus[point] === "No" ? "contained" : "outlined"
                        }
                        color="error"
                        onClick={() => handlePointStatusChange(point, "No")}
                      >
                        No
                      </Button>
                    </Stack>
                  </Stack>
                ))}
              </Box>

            <MuiTypographyMandatory   label="Comments" required  sx={{mt:3}}/>
              <StyledInputField
              
                multiline
                rows={3}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                fullWidth
                margin="normal"
              />

              {/* Professional Checklist History */}
              <Box mt={3}>
                <MUITypography variant="h3"><b>History</b></MUITypography>
                {history.length > 0 ? (
                  history.map((h, idx) => (
                    <Paper
                      key={idx}
                      sx={{
                        p: 2,
                        my: 1,
                        borderRadius: 2,
                        boxShadow: 1,
                        backgroundColor: "#fafafa",
                      }}
                    >
                      {/* Header */}
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography variant="subtitle2" color="text.secondary">
                          {formatDate(h.created_date)}
                        </Typography>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {h.updated_by}
                        </Typography>
                      </Stack>

                      {/* Comments */}
                      <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
                        <b>Comments:</b> {h.comments}
                      </Typography>

                      {/* Checklist Points */}
                      <Box
                        sx={{
                          backgroundColor: "#fff",
                          borderRadius: 1,
                          p: 1,
                        }}
                      >
                        {Object.entries(h.checklist_points_status || {}).map(
                          ([point, status], i) => (
                            <Stack
                              key={i}
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              sx={{
                                borderBottom:
                                  i !==
                                  Object.keys(h.checklist_points_status).length -
                                    1
                                    ? "1px solid #eee"
                                    : "none",
                                py: 0.5,
                              }}
                            >
                              <Typography variant="body2">{point}</Typography>
                              <Chip
                                label={status}
                                color={
                                  status === "Yes" ? "success" : "error"
                                }
                                variant="outlined"
                                size="small"
                              />
                            </Stack>
                          )
                        )}
                      </Box>
                    </Paper>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No history found.
                  </Typography>
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitUpdate}
            disabled={submitting}
          >
            {submitting ? "Updating..." : "Update"}
          </Button>
        </DialogActions>
      </Dialog>


      {selectedCheckListId && historyDrawerOpen && (
        <ReusableDrawer
          open={historyDrawerOpen}
          title="CheckList History"
          onClose={() => setHistoryDrawerOpen(false)}
          showFooter={false}
          loading={historyLoading}
        >
          <Box sx={{ p: 2 }}>
            <CheckListHistory id={selectedCheckListId} />
          </Box>
        </ReusableDrawer>
      )}

    </Paper>
  );
}

export default CheckListUpdate;
